import { ExportOutlined, LeftOutlined, SendOutlined } from '@ant-design/icons';
import type { TableColumnsType } from 'antd';
import {
  App,
  Button,
  Card,
  Descriptions,
  Form,
  Input,
  Modal,
  Result,
  Select,
  Space,
  Table,
  Tag,
} from 'antd';
import type { CSSProperties, ReactNode } from 'react';
import { useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import type { QuoteOutputProjection } from '../../../api/active/generated/quote';
import {
  formatBeijingBusinessValue,
  formatBeijingOutputValue,
} from '../../../quote-integration/beijingTime';
import { formatBeijingInstant } from '../../../quote-integration/publicShareModel';
import {
  type PublicSharePort,
  usePublicShare,
} from '../../../quote-integration/publicShareRuntime';
import { HoverImagePreview } from '../../../shared/HoverImagePreview';
import { formatMoney } from '../mock/catalog';
import { useCpq } from '../state/CpqContext';
import {
  quotationListProjectionForPublicItems,
  updatePublicShareItemsFromFrozenCell,
} from '../state/frozenQuotationProjection';
import { orderedOutputBusinessEntries } from '../state/outputTerms';
import { canUseActivePermissionAction } from '../state/permissionPolicy';
import { isReceiptCapableShareItem } from '../state/publicShareSnapshot';
import { quoteTotal } from '../state/quoteTotals';
import {
  negotiationEffectiveExpiry,
  submitShareReceipt,
} from '../state/receiptState';
import { isKnownShareStyleSnapshot } from '../state/shareStyles';
import type {
  CustomerReceiptAction,
  PublicShareItemSnapshot,
} from '../state/types';
import styles from '../styles.module.css';
import {
  FrozenQuotationHeaderSummary,
  FrozenQuotationListRenderer,
} from './FrozenQuotationListRenderer';
import {
  isQuoteOutputGroupSummaryColumn,
  quoteOutputPresentedGroupCells,
  quoteOutputRowKeys,
  quoteOutputRowSpan,
  quoteOutputSummaryIdentity,
} from './quoteOutputGrouping';

function subscribeToHashChange(listener: () => void) {
  window.addEventListener('hashchange', listener);
  return () => window.removeEventListener('hashchange', listener);
}

function readShareToken() {
  if (typeof window === 'undefined') return undefined;
  return window.location.hash.match(/^#share\/([^/?#]+)$/)?.[1];
}

const readServerShareToken = () => undefined;

function isExpiredShareSource(validUntil: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(validUntil)) return false;
  return new Date(`${validUntil}T23:59:59.999`).getTime() < Date.now();
}

/** Consume only the immutable service projection; never rehydrate from quote/catalog. */
function projectPublicShareItem(
  item: PublicShareItemSnapshot,
): PublicShareItemSnapshot {
  return { ...item };
}

export function SharePage({
  publicAccess,
}: {
  publicAccess?: { token: string; port: PublicSharePort };
}) {
  return publicAccess ? (
    <PublicShareContent {...publicAccess} />
  ) : (
    <LegacyShareContent />
  );
}

type ShareVisualStyle = {
  styleId: string;
  name: string;
  themeColor: string;
  titleBackground: string;
  titleFontSize: number;
  logoImage: string;
  coverImage: string;
  identityTextColor?: string;
};

/** The original customer-page layout, shared by the existing local and API ports. */
function SharePageFrame({
  visual,
  expiryLabel,
  customerView = false,
  fullWidth = false,
  children,
}: {
  visual: ShareVisualStyle;
  expiryLabel: ReactNode;
  customerView?: boolean;
  fullWidth?: boolean;
  children: ReactNode;
}) {
  const shareStyleVariables = {
    '--share-theme-color': visual.themeColor,
    '--share-title-background': visual.titleBackground,
    '--share-title-font-size': `${visual.titleFontSize}px`,
  } as CSSProperties;
  return (
    <div
      className={`${styles.sharePage}${customerView ? ` ${styles.publicSharePage}` : ''}${fullWidth ? ` ${styles.publicSharePageFullWidth}` : ''}`}
      style={shareStyleVariables}
      data-share-style-id={visual.styleId}
    >
      {(!customerView || visual.logoImage || visual.coverImage) && (
        <div
          className={styles.shareBanner}
          style={{
            backgroundColor: visual.titleBackground,
            borderColor: visual.themeColor,
          }}
        >
          <div className={styles.shareStyleIdentity}>
            {visual.logoImage ? (
              <HoverImagePreview
                sources={[visual.logoImage]}
                alt={`${visual.name} Logo`}
                frameClassName={styles.shareStyleLogo}
                imageStyle={{ objectFit: 'contain' }}
              />
            ) : null}
            {!customerView && (
              <strong
                style={{
                  color: visual.identityTextColor ?? visual.themeColor,
                  fontSize: `${visual.titleFontSize}px`,
                }}
              >
                {visual.name} · 客户报价
              </strong>
            )}
            {visual.coverImage ? (
              <HoverImagePreview
                sources={[visual.coverImage]}
                alt={`${visual.name} 封面`}
                frameClassName={styles.shareStyleCover}
              />
            ) : null}
          </div>
          {!customerView && <span>{expiryLabel}</span>}
        </div>
      )}
      <div className={styles.shareContent}>{children}</div>
    </div>
  );
}

function SharePageHeading({
  quoteNo,
  version,
  children,
}: {
  quoteNo: string;
  version: string | number;
  children: ReactNode;
}) {
  return (
    <div className={styles.managementHeader}>
      <div>
        <h2>设备报价清单</h2>
        <p>
          报价编号：{quoteNo} V{version}
        </p>
      </div>
      <Space>{children}</Space>
    </div>
  );
}

function publicOutputColumnTotal(
  projection: QuoteOutputProjection,
  key: string,
  valueType: string,
  groupCells: ReturnType<typeof quoteOutputPresentedGroupCells>,
): number | null {
  if (valueType !== 'money' && valueType !== 'integer') return null;
  const countedGroups = new Set<string>();
  const values = projection.rows.flatMap((row, rowIndex) => {
    // Plan sets are displayed independently from device quantities and amounts.
    if (row.rowKind === 'empty-plan') return [];
    const span = groupCells.get(`${rowIndex}:${key}`);
    if (span?.rowSpan === 0) return [];
    const value = span ? span.value : row.values[key];
    const mergedGroupPrice =
      valueType === 'money' && isQuoteOutputGroupSummaryColumn(key);
    if (mergedGroupPrice && value === '-') return [];
    // 旧冻结投影可能把同一套餐实例拆成多个连续段；行合并只负责展示，
    // 套餐摘要合计须按实例与金额去重，不能用名称合并独立实例或设备金额。
    if (row.groupId && isQuoteOutputGroupSummaryColumn(key)) {
      const identity = JSON.stringify([
        quoteOutputSummaryIdentity(
          row,
          key,
          projection.fieldCombination.businessContent,
        ),
        value,
      ]);
      if (countedGroups.has(identity)) return [];
      countedGroups.add(identity);
    }
    if (key === 'retail_price_tax13') {
      const quantity = row.values.quantity;
      const lineCents =
        typeof value === 'number' && typeof quantity === 'number'
          ? Math.round(value * 100) * quantity
          : Number.NaN;
      return [
        typeof quantity === 'number' &&
        Number.isSafeInteger(quantity) &&
        quantity > 0 &&
        Number.isSafeInteger(lineCents)
          ? lineCents / 100
          : null,
      ];
    }
    return [value];
  });
  if (
    values.length === 0 ||
    values.some((value) => typeof value !== 'number' || !Number.isFinite(value))
  )
    return null;
  if (valueType === 'integer')
    return (values as number[]).reduce((sum, value) => sum + value, 0);
  return (
    (values as number[]).reduce(
      (sum, value) => sum + Math.round(value * 100),
      0,
    ) / 100
  );
}

function publicOutputValue(
  key: string,
  value: string | number | null,
  showCurrency: boolean,
): string {
  if (value === null || value === '') return '—';
  if (typeof value === 'number')
    return showCurrency ? `¥${value.toFixed(2)}` : String(value);
  return formatBeijingBusinessValue(key, value) ?? value;
}

type PublicOutputPresentation = {
  detailRowHeight: number;
  imageMaxSize: number;
  productNameNoWrap: boolean;
  identifier: { color: string; fontSize: number };
  marketing: {
    layout: 'legacy-highlight' | 'plain';
    headerFill: string;
    headerText: string;
    bodyFill: string;
    primaryText: string;
    secondaryText: string;
  };
  rentalTerms: {
    headerFill: string;
    headerText: string;
    valueText: string;
  };
};

function publicOutputPresentation(projection: QuoteOutputProjection) {
  return (
    projection as QuoteOutputProjection & {
      createdAt?: string;
      presentation?: PublicOutputPresentation;
    }
  ).presentation;
}

/** Preview fallback mirrors the server's quoteOutputPresentation defaults. */
export function previewOutputPresentation(
  styleId: string,
): PublicOutputPresentation {
  return {
    detailRowHeight: 26,
    imageMaxSize: 24,
    productNameNoWrap: true,
    identifier: { color: '#98A2B3', fontSize: 10 },
    marketing: {
      layout: [
        'STYLE-THREE-STORE',
        'STYLE-TWO-STORE',
        'STYLE-PURCHASE-TAX',
        'STYLE-THREE-STORE-FLIP',
      ].includes(styleId)
        ? 'legacy-highlight'
        : 'plain',
      headerFill: '#FFF1E5',
      headerText: '#8A421B',
      bodyFill: '#FFF9F1',
      primaryText: '#C62F26',
      secondaryText: '#334155',
    },
    rentalTerms: {
      headerFill: '#E9AA79',
      headerText: '#111827',
      valueText: '#5F6368',
    },
  };
}

function publicMarketingLines(value: string) {
  let characterOffset = 0;
  return value.split(/\r?\n/).map((line) => {
    characterOffset += line.length + 1;
    return { key: String(characterOffset), text: line };
  });
}

/** The same frozen-projection renderer is used by preview and published links. */
export function QuoteOutputDocument({
  projection,
  versionSelector,
  renderImage,
}: {
  projection: QuoteOutputProjection;
  versionSelector?: ReactNode;
  renderImage?: (
    source: string,
    row: QuoteOutputProjection['rows'][number],
    label: string,
    columnKey: string,
    rowIndex: number,
  ) => ReactNode;
}) {
  const presentation =
    publicOutputPresentation(projection) ??
    previewOutputPresentation(projection.style.id);
  const groupCells = quoteOutputPresentedGroupCells(projection);
  const outputRowKey = quoteOutputRowKeys(projection.rows);
  const tableColumns: TableColumnsType<(typeof projection.rows)[number]> =
    projection.columns.map((column) => ({
      key: column.key,
      title: column.label,
      width:
        column.valueType === 'image'
          ? Math.max(48, Math.round(column.width * 5.4))
          : Math.max(54, Math.round(column.width * 5.4)),
      render: (_value, row, rowIndex) => {
        const presentedCell = groupCells.get(`${rowIndex}:${column.key}`);
        const value = presentedCell
          ? presentedCell.value
          : row.values[column.key];
        if (
          column.valueType === 'image' &&
          (value === null || value === undefined || value === '')
        )
          return null;
        if (value === null || value === undefined || value === '') return '—';
        if (column.valueType === 'image' && typeof value === 'string')
          return renderImage ? (
            renderImage(value, row, column.label, column.key, rowIndex)
          ) : (
            <HoverImagePreview
              sources={[value]}
              alt={`${column.label} 图片`}
              frameClassName={styles.quoteOutputImage}
              imageStyle={{ objectFit: 'contain' }}
            />
          );
        if (column.valueType === 'money' && typeof value === 'number')
          return value.toFixed(2);
        const builtinDate = formatBeijingOutputValue(column.key, value);
        if (builtinDate !== null) return builtinDate;
        if (column.valueType === 'date' && typeof value === 'string')
          return Number.isFinite(Date.parse(value))
            ? formatBeijingInstant(value)
            : value;
        return String(value);
      },
      onHeaderCell: () => ({
        style: {
          background: projection.style.themeColor,
          color: projection.style.headerTextColor,
        },
      }),
      onCell: (_row, rowIndex) => {
        const rowSpan =
          groupCells.get(`${rowIndex ?? 0}:${column.key}`)?.rowSpan ?? 1;
        return {
          rowSpan,
          className: [
            rowSpan > 1 ? styles.quoteOutputGroupedCell : undefined,
            column.key === 'product_name' && presentation.productNameNoWrap
              ? styles.publicOutputProductName
              : undefined,
          ]
            .filter(Boolean)
            .join(' '),
        };
      },
    }));
  const createdAt = (projection as typeof projection & { createdAt?: string })
    .createdAt;
  const outputStyle = {
    '--public-output-header': projection.style.themeColor,
    '--public-output-header-text': projection.style.headerTextColor,
    '--public-output-row-height': `${presentation.detailRowHeight}px`,
    '--public-output-image-size': `${presentation.imageMaxSize}px`,
    '--public-output-identifier-color': presentation.identifier.color,
    '--public-output-identifier-size': `${presentation.identifier.fontSize}px`,
    '--public-output-marketing-border': presentation.marketing.headerFill,
    '--public-output-marketing-background': presentation.marketing.bodyFill,
    '--public-output-marketing-title': presentation.marketing.headerText,
    '--public-output-marketing-highlight': presentation.marketing.primaryText,
    '--public-output-marketing-text': presentation.marketing.secondaryText,
    '--public-output-terms-header': presentation.rentalTerms.headerFill,
    '--public-output-terms-header-text': presentation.rentalTerms.headerText,
    '--public-output-terms-value-text': presentation.rentalTerms.valueText,
  } as CSSProperties;
  return (
    <section className={styles.publicOutputDocument} style={outputStyle}>
      <div className={styles.publicOutputIdentity}>
        <span>报价编号：{projection.title}</span>
        {createdAt && Number.isFinite(Date.parse(createdAt)) ? (
          <span>创建时间：{formatBeijingInstant(createdAt)}</span>
        ) : null}
        {versionSelector}
      </div>
      <p className={styles.publicQuoteScrollHint}>
        左右滑动报价表格可查看全部字段
      </p>
      <section
        className={styles.quoteOutputTableViewport}
        data-quote-output-viewport
        data-frozen-quotation-list="v1"
        aria-label="报价明细表格，可横向滚动查看全部字段"
        // biome-ignore lint/a11y/noNoninteractiveTabindex: 客户窄屏需要用键盘聚焦后横向滚动报价表。
        tabIndex={0}
      >
        <Table
          rowKey={outputRowKey}
          columns={tableColumns}
          dataSource={projection.rows}
          pagination={false}
          scroll={{ x: 'max-content' }}
          summary={() =>
            projection.rows.length > 0 ? (
              <Table.Summary.Row className={styles.publicOutputTotal}>
                {projection.columns.map((column, index) => {
                  const total = publicOutputColumnTotal(
                    projection,
                    column.key,
                    column.valueType,
                    groupCells,
                  );
                  return (
                    <Table.Summary.Cell key={column.key} index={index}>
                      {index === 0
                        ? '合计'
                        : total === null
                          ? null
                          : column.valueType === 'money'
                            ? total.toFixed(2)
                            : total}
                    </Table.Summary.Cell>
                  );
                })}
              </Table.Summary.Row>
            ) : null
          }
        />
      </section>
      {projection.businessSections.map((section) => {
        const entries = orderedOutputBusinessEntries(
          section.kind,
          section.values,
        );
        if (section.kind === 'marketing') {
          const marketingText = entries
            .map(([, value]) => (value === null ? '' : String(value)))
            .join('\n');
          return (
            <section
              key={section.kind}
              aria-label="营销活动说明"
              className={`${styles.publicOutputMarketing}${presentation.marketing.layout !== 'legacy-highlight' ? ` ${styles.publicOutputMarketingPlain}` : ''}`}
            >
              <strong>营销活动说明</strong>
              <div>
                {publicMarketingLines(marketingText).map((line) => (
                  <p key={line.key}>{line.text || '\u00a0'}</p>
                ))}
              </div>
            </section>
          );
        }
        return (
          <section
            key={section.kind}
            aria-label={
              section.kind === 'rental-terms' ? '租赁信息' : '购买信息'
            }
            className={styles.publicOutputTerms}
          >
            <div
              className={styles.publicOutputTermsViewport}
              data-quote-output-viewport
            >
              <table>
                <thead>
                  <tr>
                    {entries.map(([key]) => (
                      <th key={key} scope="col">
                        {key}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    {entries.map(([key, value]) => (
                      <td key={key} data-label={key}>
                        {publicOutputValue(key, value, false)}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        );
      })}
    </section>
  );
}

function PublicShareContent({
  port,
  token,
}: {
  port: PublicSharePort;
  token: string;
}) {
  const [selectedVersion, setSelectedVersion] = useState<number>();
  const [latestVersion, setLatestVersion] = useState<number>();
  const { state, retry } = usePublicShare(port, token, selectedVersion);
  useEffect(() => {
    if (state.status !== 'ready') return;
    const current = Number(state.value.customerVersionNo);
    if (selectedVersion === undefined) setLatestVersion(current);
  }, [selectedVersion, state]);
  if (state.status !== 'ready')
    return (
      <Result
        status={state.status === 'error' ? 'warning' : 'info'}
        title={state.status === 'error' ? '分享链接不可用' : '正在读取客户报价'}
        subTitle={
          state.status === 'error'
            ? state.message
            : '正在读取服务端当前客户版本。'
        }
        extra={
          state.status === 'error' ? (
            <Button onClick={retry}>重试</Button>
          ) : undefined
        }
      />
    );
  const share = state.value;
  const projection = share.outputProjection;
  const presentation = publicOutputPresentation(projection);
  const outputRowKey = quoteOutputRowKeys(projection.rows);
  const visual: ShareVisualStyle = {
    styleId: projection.style.id,
    name: projection.style.name,
    themeColor: projection.style.themeColor,
    titleBackground: projection.style.titleBackground,
    titleFontSize: 22,
    logoImage: '',
    coverImage: '',
    identityTextColor: projection.style.headerTextColor,
  };
  const tableColumns: TableColumnsType<(typeof projection.rows)[number]> =
    projection.columns.map((column) => ({
      key: column.key,
      title: column.label,
      width:
        column.valueType === 'image'
          ? Math.min(80, Math.max(64, column.width * 8))
          : Math.max(96, column.width * 8),
      render: (_value, row) => {
        const value = row.values[column.key];
        if (
          column.valueType === 'image' &&
          (value === null || value === undefined || value === '')
        )
          return null;
        if (value === null || value === undefined || value === '') return '—';
        if (column.valueType === 'image' && typeof value === 'string')
          return (
            <HoverImagePreview
              sources={[value]}
              alt={`${column.label} 图片`}
              frameClassName={styles.quoteOutputImage}
              imageStyle={{ objectFit: 'contain' }}
            />
          );
        if (column.valueType === 'money' && typeof value === 'number')
          return `¥${value.toFixed(2)}`;
        const builtinDate = formatBeijingOutputValue(column.key, value);
        if (builtinDate !== null) return builtinDate;
        if (column.valueType === 'date' && typeof value === 'string')
          return Number.isFinite(Date.parse(value))
            ? formatBeijingInstant(value)
            : value;
        return String(value);
      },
      onHeaderCell: () => ({
        style: {
          background: projection.style.headerBackground,
          color: projection.style.headerTextColor,
        },
      }),
      onCell: (_row, rowIndex) => {
        const rowSpan = quoteOutputRowSpan(
          projection.rows,
          rowIndex ?? 0,
          column.key,
        );
        return {
          rowSpan,
          className: rowSpan > 1 ? styles.quoteOutputGroupedCell : undefined,
        };
      },
    }));
  const versionCeiling = latestVersion ?? Number(share.customerVersionNo);
  const versionSelector = (
    <Space wrap className={styles.publicVersionSelector}>
      <span>历史客户版本</span>
      <Select
        aria-label="客户报价历史版本"
        value={Number(share.customerVersionNo)}
        options={Array.from({ length: versionCeiling }, (_, index) => ({
          value: index + 1,
          label: `V${index + 1}${index + 1 === versionCeiling ? '（当前）' : ''}`,
        }))}
        onChange={(value) =>
          setSelectedVersion(value === versionCeiling ? undefined : value)
        }
      />
    </Space>
  );
  return (
    <SharePageFrame
      visual={visual}
      customerView
      fullWidth={Boolean(presentation)}
      expiryLabel={`只读报价 · 链接访问截止 ${formatBeijingInstant(share.effectiveExpiresAt)}（北京时间）`}
    >
      {!presentation ? (
        <>
          {versionSelector}
          <Card className={styles.publicQuoteCard}>
            <p className={styles.publicQuoteScrollHint}>
              左右滑动报价表格可查看全部字段
            </p>
            <section
              className={styles.quoteOutputTableViewport}
              data-frozen-quotation-list="v1"
              aria-label="报价明细表格，可横向滚动查看全部字段"
              // biome-ignore lint/a11y/noNoninteractiveTabindex: 客户窄屏需要用键盘聚焦后横向滚动报价表。
              tabIndex={0}
            >
              <Table
                rowKey={outputRowKey}
                columns={tableColumns}
                dataSource={projection.rows}
                pagination={false}
                scroll={{ x: 'max-content' }}
              />
            </section>
          </Card>
          {projection.businessSections.map((section) => (
            <Card
              key={section.kind}
              className={styles.sectionCard}
              title={
                section.kind === 'rental-terms'
                  ? '租赁信息'
                  : section.kind === 'purchase-terms'
                    ? '购买信息'
                    : '营销活动说明'
              }
            >
              <Descriptions
                size="small"
                column={{ xs: 1, sm: 2, md: 3 }}
                items={orderedOutputBusinessEntries(
                  section.kind,
                  section.values,
                ).map(([key, value]) => ({
                  key,
                  label: key,
                  children:
                    value === null || value === ''
                      ? '—'
                      : typeof value === 'number'
                        ? `¥${value.toFixed(2)}`
                        : (formatBeijingBusinessValue(key, value) ??
                          String(value)),
                }))}
              />
            </Card>
          ))}
        </>
      ) : (
        <QuoteOutputDocument
          projection={projection}
          versionSelector={versionSelector}
        />
      )}
    </SharePageFrame>
  );
}

function LegacyShareContent() {
  const { message } = App.useApp();
  const { state, commit } = useCpq();
  const canReturnToWorkbench = canUseActivePermissionAction(
    state,
    'workspace',
    'workspace:quote.read',
  );
  const hashToken = useSyncExternalStore(
    subscribeToHashChange,
    readShareToken,
    readServerShareToken,
  );
  const link = state.negotiationLinks?.find((item) => item.token === hashToken);
  const publication = link?.currentPublicationId
    ? state.publications?.find((item) => item.id === link.currentPublicationId)
    : undefined;
  const publications = link
    ? (state.publications ?? []).filter((item) => item.linkId === link.id)
    : [];
  const historyPublicationId = undefined;
  const visiblePublication =
    publications.find((item) => item.id === historyPublicationId) ??
    publication;
  const viewingHistory = Boolean(
    visiblePublication &&
      publication &&
      visiblePublication.id !== publication.id,
  );
  // Once the modern graph exists, shares are historical import material only,
  // never a second URL or receipt-write source.
  const legacyShare =
    !state.negotiationLinks && !state.publications
      ? state.shares.find((item) => item.token === hashToken)
      : undefined;
  const effectivePublication = visiblePublication ?? publication;
  const share =
    effectivePublication && link
      ? {
          id: link.id,
          token: link.token,
          quoteId: effectivePublication.quoteId,
          quoteVersion: effectivePublication.quoteVersion,
          allowSelection: effectivePublication.allowSelection,
          snapshotPolicy: effectivePublication.snapshotPolicy,
          customerSnapshot: effectivePublication.customerSnapshot,
          validUntilSnapshot: effectivePublication.validUntilSnapshot,
          itemsSnapshot: effectivePublication.itemsSnapshot,
          overallDiscountSnapshot: effectivePublication.overallDiscountSnapshot,
          selectionOptionsSnapshot:
            effectivePublication.selectionOptionsSnapshot,
          combinationSnapshot: effectivePublication.combinationSnapshot,
          quotationListProjection: effectivePublication.quotationListProjection,
          headerProjection: effectivePublication.headerProjection,
          businessSectionsSnapshot:
            effectivePublication.businessSectionsSnapshot,
          shareStyleSnapshot: effectivePublication.shareStyleSnapshot,
          status:
            link.lifecycle === 'revoked'
              ? ('revoked' as const)
              : ('active' as const),
          createdAt: effectivePublication.createdAt,
        }
      : legacyShare;
  const frozenStyle =
    effectivePublication?.shareStyleSnapshot ?? share?.shareStyleSnapshot;
  const frozenCombination =
    effectivePublication?.quotationListProjection?.combinationSnapshot ??
    share?.quotationListProjection?.combinationSnapshot;
  const frozenCustomer =
    effectivePublication?.customerSnapshot ?? share?.customerSnapshot;
  const frozenListProjection =
    effectivePublication?.quotationListProjection ??
    share?.quotationListProjection;
  const frozenHeaderProjection =
    effectivePublication?.headerProjection ?? share?.headerProjection;
  const existing = share
    ? state.receipts.find((item) =>
        visiblePublication
          ? item.publicationId === visiblePublication.id
          : item.shareId === share.id,
      )
    : undefined;
  const savedDraft =
    link && publication && !viewingHistory
      ? state.negotiationDrafts.find(
          (draft) =>
            draft.linkId === link.id && draft.publicationId === publication.id,
        )
      : undefined;
  const [items, setItems] = useState<PublicShareItemSnapshot[]>(
    () =>
      savedDraft?.items.map(projectPublicShareItem) ??
      visiblePublication?.itemsSnapshot.map(projectPublicShareItem) ??
      share?.itemsSnapshot?.map(projectPublicShareItem) ??
      [],
  );
  const [name, setName] = useState(
    existing?.companyOrName ??
      savedDraft?.companyOrName ??
      frozenCustomer?.name ??
      '演示客户 A',
  );
  const [phone, setPhone] = useState(
    existing?.phone ??
      savedDraft?.phone ??
      frozenCustomer?.phone ??
      '13800000000',
  );
  const [note, setNote] = useState(
    existing?.note ?? savedDraft?.note ?? '设备数量已确认，请销售联系。',
  );
  const [customerAction, setCustomerAction] = useState<CustomerReceiptAction>(
    savedDraft?.customerAction ?? 'request_change',
  );
  const [picker, setPicker] = useState<{
    mode: 'add' | 'replace';
    itemId?: string;
  } | null>(null);
  const [remarkItemId, setRemarkItemId] = useState<string | null>(null);
  const [remarkDraft, setRemarkDraft] = useState('');
  const [expiryClock, setExpiryClock] = useState(() => Date.now());
  const [customerListChanged, setCustomerListChanged] = useState(
    Boolean(savedDraft),
  );

  useEffect(() => {
    setItems(
      savedDraft?.items.map(projectPublicShareItem) ??
        visiblePublication?.itemsSnapshot.map(projectPublicShareItem) ??
        share?.itemsSnapshot?.map(projectPublicShareItem) ??
        [],
    );
    setName(
      existing?.companyOrName ??
        savedDraft?.companyOrName ??
        frozenCustomer?.name ??
        '演示客户 A',
    );
    setPhone(
      existing?.phone ??
        savedDraft?.phone ??
        frozenCustomer?.phone ??
        '13800000000',
    );
    setNote(
      existing?.note ?? savedDraft?.note ?? '设备数量已确认，请销售联系。',
    );
    setCustomerAction(savedDraft?.customerAction ?? 'request_change');
    setPicker(null);
    setRemarkItemId(null);
    setRemarkDraft('');
    setCustomerListChanged(Boolean(savedDraft));
  }, [
    existing?.id,
    frozenCustomer?.customerId,
    frozenCustomer?.name,
    frozenCustomer?.phone,
    savedDraft?.updatedAt,
    share?.id,
    visiblePublication?.id,
  ]);

  const effectiveExpiry = link
    ? negotiationEffectiveExpiry(link, publication)
    : frozenHeaderProjection?.validUntilSnapshot
      ? `${frozenHeaderProjection.validUntilSnapshot}T23:59:59.999`
      : undefined;
  useEffect(() => {
    if (!effectiveExpiry) return;
    const expiresAt = new Date(effectiveExpiry).getTime();
    if (!Number.isFinite(expiresAt)) return;
    const expire = () => {
      setItems([]);
      setName('');
      setPhone('');
      setNote('');
      setPicker(null);
      setRemarkItemId(null);
      setRemarkDraft('');
      setExpiryClock(Date.now());
    };
    const remaining = expiresAt - Date.now();
    if (remaining <= 0) {
      expire();
      return;
    }
    const timer = window.setTimeout(
      expire,
      Math.min(remaining + 1, 2_147_000_000),
    );
    return () => window.clearTimeout(timer);
  }, [effectiveExpiry]);

  const displayProjection = useMemo(() => {
    if (!frozenListProjection)
      return { projection: undefined, error: undefined };
    if (!customerListChanged)
      return { projection: frozenListProjection, error: undefined };
    try {
      return {
        projection: quotationListProjectionForPublicItems(
          frozenListProjection,
          items,
        ),
        error: undefined,
      };
    } catch (error) {
      return {
        projection: undefined,
        error:
          error instanceof Error ? error.message : '冻结报价清单投影不可用',
      };
    }
  }, [customerListChanged, frozenListProjection, items]);
  const currentSelectionTotal = useMemo(() => {
    if (!frozenHeaderProjection) return undefined;
    try {
      return quoteTotal({
        items,
        overallDiscount: frozenHeaderProjection.overallDiscountSnapshot,
      });
    } catch {
      return undefined;
    }
  }, [frozenHeaderProjection, items]);

  const expired =
    Boolean(
      link?.lifecycle === 'expired' ||
        (effectiveExpiry && new Date(effectiveExpiry).getTime() < expiryClock),
    ) ||
    (!link &&
      Boolean(frozenHeaderProjection) &&
      isExpiredShareSource(frozenHeaderProjection?.validUntilSnapshot ?? ''));
  const returnToSystemHome = () =>
    commit((current) => {
      window.history.replaceState(
        null,
        '',
        `${window.location.pathname}${window.location.search}`,
      );
      return { ...current, activeView: 'workspace' };
    });
  const returnLabel = canReturnToWorkbench
    ? '返回报价工作台'
    : '返回客户侧历史';
  const visibleListProjection = displayProjection.projection;

  if (
    share?.snapshotPolicy !== 'published-channel-v1' ||
    !share?.itemsSnapshot ||
    !frozenCustomer ||
    !share.validUntilSnapshot ||
    share.overallDiscountSnapshot === undefined ||
    !isKnownShareStyleSnapshot(state, frozenStyle) ||
    !frozenCombination ||
    !frozenListProjection ||
    !frozenHeaderProjection ||
    !visibleListProjection ||
    link?.lifecycle === 'paused' ||
    share?.status === 'revoked' ||
    expired
  ) {
    const subTitle =
      link?.lifecycle === 'paused'
        ? '该客户链接已暂停，不能查看内容或提交回执。'
        : share?.status === 'revoked'
          ? '该分享已撤销，不能查看内容或提交回执。'
          : expired
            ? '该分享绑定的报价已过有效期，不能查看内容或提交回执。'
            : share
              ? (displayProjection.error ??
                '该分享缺少可验证的冻结报价清单或抬头投影。')
              : '请核对完整分享链接后重试。';
    return (
      <Result
        status="warning"
        title="分享链接不可用"
        subTitle={subTitle}
        extra={
          <Button icon={<LeftOutlined />} onClick={returnToSystemHome}>
            {returnLabel}
          </Button>
        }
      />
    );
  }

  const updateItem = (
    itemId: string,
    patch: Partial<PublicShareItemSnapshot>,
  ) => {
    if (!share.allowSelection) return;
    setCustomerListChanged(true);
    setItems((current) =>
      current.map((item) =>
        item.itemId === itemId ? { ...item, ...patch } : item,
      ),
    );
  };

  const chooseDevice = (deviceId: string) => {
    const device = share.selectionOptionsSnapshot?.find(
      (candidate) => candidate.deviceId === deviceId,
    );
    if (!device) return;
    if (!picker) return;
    if (picker.mode === 'replace' && picker.itemId) {
      updateItem(picker.itemId, { replacementDeviceId: device.deviceId });
    } else {
      setCustomerListChanged(true);
      setItems((current) => [
        ...current,
        {
          ...device,
          itemId: `PUBLIC-${device.deviceId}-${current.length + 1}`,
          sourceType: 'receipt',
          sourceId: share.id,
          sourceName: '第三方新增',
        },
      ]);
    }
    setPicker(null);
  };

  const submitReceipt = () => {
    if (!name || !/^1\d{10}$/.test(phone)) {
      message.error('请填写公司/姓名和 11 位手机号');
      return;
    }
    if (!items.every(isReceiptCapableShareItem)) {
      message.error('公开通道未批准完整选配字段，本分享仅可只读');
      return;
    }
    commit((current) =>
      submitShareReceipt(current, {
        shareId: share.id,
        linkId: link?.id,
        publicationId: publication?.id,
        customerAction,
        companyOrName: name,
        phone,
        note,
        items,
      }),
    );
  };

  const saveCustomerDraft = () => {
    if (!link || !publication || viewingHistory || existing) return;
    commit((current) => {
      const currentLink = current.negotiationLinks?.find(
        (candidate) => candidate.id === link.id,
      );
      if (
        currentLink?.lifecycle !== 'active' ||
        currentLink.currentPublicationId !== publication.id
      )
        throw new Error('链接状态或当前发布已变化，草稿未保存');
      const draft = {
        id: `DRAFT-${link.id}-${publication.id}`,
        linkId: link.id,
        publicationId: publication.id,
        customerId: publication.customerSnapshot.customerId,
        customerAction,
        companyOrName: name,
        phone,
        note,
        items: structuredClone(items),
        updatedAt: new Date().toISOString(),
      };
      return {
        ...current,
        negotiationDrafts: [
          ...current.negotiationDrafts.filter((item) => item.id !== draft.id),
          draft,
        ],
      };
    });
    message.success('客户选择草稿已保存，可刷新后继续');
  };

  return (
    <SharePageFrame
      visual={frozenStyle}
      expiryLabel={
        <>
          {share.allowSelection ? '可选配报价' : '只读报价'} ·{' '}
          {effectiveExpiry ?? share.validUntilSnapshot} 前有效
        </>
      }
    >
      <Button icon={<LeftOutlined />} onClick={returnToSystemHome}>
        {returnLabel}
      </Button>
      <SharePageHeading
        quoteNo={frozenListProjection.quoteId}
        version={frozenListProjection.quoteVersion}
      >
        {share.allowSelection && !existing && !viewingHistory ? (
          <Button onClick={() => setPicker({ mode: 'add' })}>新增设备</Button>
        ) : null}
        <Tag color={share.allowSelection ? 'purple' : 'default'}>
          {share.allowSelection ? '允许选配' : '只读'}
        </Tag>
        <Tag title="仅保留在当前浏览器，不是公开互联网分享链接">本地演示</Tag>
        {existing && visiblePublication ? (
          <Tag
            title={`回执 ${existing.id} · 报价编号 ${existing.sourceQuoteId} · 提交于 ${formatBeijingInstant(existing.createdAt)}（北京时间）`}
          >
            已提交 · 只读
          </Tag>
        ) : null}
        <Tag data-share-field-combination-id={frozenCombination.id}>
          字段组合：{frozenCombination.name}
        </Tag>
        <FrozenQuotationHeaderSummary
          listProjection={frozenListProjection}
          headerProjection={frozenHeaderProjection}
        />
        {currentSelectionTotal !== undefined ? (
          <Tag>当前选择试算：{formatMoney(currentSelectionTotal)}</Tag>
        ) : null}
      </SharePageHeading>
      <Card>
        <FrozenQuotationListRenderer
          projection={visibleListProjection}
          editable={share.allowSelection && !existing && !viewingHistory}
          onCellChange={(change) => {
            try {
              setItems((current) =>
                updatePublicShareItemsFromFrozenCell(
                  frozenListProjection,
                  current,
                  change,
                ),
              );
              setCustomerListChanged(true);
            } catch (error) {
              message.error(
                error instanceof Error ? error.message : '客户字段修改失败',
              );
            }
          }}
        />
      </Card>
      {share.businessSectionsSnapshot?.map((section) => (
        <Card
          key={section.kind}
          title="报价条款"
          className={styles.sectionCard}
        >
          <Descriptions
            size="small"
            column={{ xs: 1, sm: 2, md: 3 }}
            items={orderedOutputBusinessEntries(
              section.kind,
              section.values,
            ).map(([key, value]) => ({
              key,
              label: key,
              children:
                value === null || value === ''
                  ? '—'
                  : typeof value === 'number'
                    ? `¥${value.toFixed(2)}`
                    : (formatBeijingBusinessValue(key, value) ?? String(value)),
            }))}
          />
        </Card>
      ))}
      {share.allowSelection ? (
        <Card title="选配调整" className={styles.sectionCard}>
          <Space orientation="vertical" size={8} style={{ width: '100%' }}>
            {items.map((item, index) => (
              <Space key={item.itemId} wrap>
                <Tag>第 {index + 1} 项</Tag>
                {item.sourceType !== 'receipt' ? (
                  <Button
                    size="small"
                    disabled={!!existing || viewingHistory}
                    onClick={() =>
                      setPicker({ mode: 'replace', itemId: item.itemId })
                    }
                  >
                    替换设备
                  </Button>
                ) : null}
                {item.replacementDeviceId ? (
                  <Button
                    size="small"
                    disabled={!!existing || viewingHistory}
                    onClick={() =>
                      updateItem(item.itemId, {
                        replacementDeviceId: undefined,
                      })
                    }
                  >
                    恢复原设备
                  </Button>
                ) : null}
                <Button
                  size="small"
                  disabled={!!existing || viewingHistory}
                  onClick={() => {
                    setRemarkItemId(item.itemId);
                    setRemarkDraft(item.remark ?? '');
                  }}
                >
                  备注{item.remark ? ' ✓' : ''}
                </Button>
                {item.sourceType === 'receipt' ? (
                  <Button
                    size="small"
                    danger
                    disabled={!!existing || viewingHistory}
                    onClick={() => {
                      setCustomerListChanged(true);
                      setItems((current) =>
                        current.filter((entry) => entry.itemId !== item.itemId),
                      );
                    }}
                  >
                    删除
                  </Button>
                ) : null}
              </Space>
            ))}
          </Space>
        </Card>
      ) : null}
      {share.allowSelection ? (
        <Card title="提交选择结果" className={styles.sectionCard}>
          {viewingHistory ? <Tag>历史版本 · 只读</Tag> : null}
          {existing ? (
            <Tag
              color={existing.customerAction === 'accept' ? 'green' : 'blue'}
              title={
                existing.customerAction === 'accept'
                  ? `已确认 V${share.quoteVersion}，无需重复提交。`
                  : `回执 ${existing.id} 已锁定当前版本；销售更新下一版本后，可在同一固定链接再次反馈或确认。`
              }
            >
              {existing.customerAction === 'accept'
                ? '本轮报价已确认'
                : '客户反馈已提交，等待销售更新'}
            </Tag>
          ) : (
            <Form layout="vertical">
              <Form.Item label="本轮协商决定" required>
                <Space wrap>
                  <Button
                    disabled={viewingHistory}
                    type={customerAction === 'accept' ? 'primary' : 'default'}
                    onClick={() => setCustomerAction('accept')}
                  >
                    接受本轮
                  </Button>
                  <Button
                    disabled={viewingHistory}
                    type={
                      customerAction === 'request_change'
                        ? 'primary'
                        : 'default'
                    }
                    onClick={() => setCustomerAction('request_change')}
                  >
                    要求修改
                  </Button>
                  <Button
                    disabled={viewingHistory}
                    type={
                      customerAction === 'temporarily_reject'
                        ? 'primary'
                        : 'default'
                    }
                    onClick={() => setCustomerAction('temporarily_reject')}
                  >
                    暂不接受
                  </Button>
                </Space>
              </Form.Item>
              <Form.Item label="公司或姓名" required>
                <Input
                  value={name}
                  disabled={viewingHistory}
                  onChange={(event) => setName(event.target.value)}
                />
              </Form.Item>
              <Form.Item label="手机" required>
                <Input
                  value={phone}
                  disabled={viewingHistory}
                  onChange={(event) => setPhone(event.target.value)}
                />
              </Form.Item>
              <Form.Item label="客户留言">
                <Input.TextArea
                  aria-label="客户留言"
                  rows={4}
                  value={note}
                  disabled={viewingHistory}
                  onChange={(event) => setNote(event.target.value)}
                />
              </Form.Item>
              <Space>
                <Button
                  aria-label="保存客户草稿"
                  disabled={viewingHistory}
                  onClick={saveCustomerDraft}
                >
                  保存
                </Button>
                <Button
                  icon={<ExportOutlined />}
                  disabled
                  title="公开分享页无稳定 actor/resource 上下文，导出待正式服务接入"
                >
                  导出表格（待接入）
                </Button>
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  disabled={viewingHistory}
                  onClick={submitReceipt}
                >
                  提交选择结果
                </Button>
              </Space>
            </Form>
          )}
        </Card>
      ) : null}
      <Modal
        open={picker !== null}
        title={picker?.mode === 'replace' ? '替换设备' : '新增设备'}
        footer={null}
        onCancel={() => setPicker(null)}
        destroyOnHidden
      >
        <Input.Search
          placeholder="搜索设备名称或 SKU"
          style={{ marginBottom: 12 }}
        />
        <div className={styles.publicPickerList}>
          {(share.selectionOptionsSnapshot ?? []).map((device) => (
            <button
              key={device.deviceId}
              type="button"
              onClick={() => chooseDevice(device.deviceId ?? '')}
            >
              <span>
                <strong>{device.name}</strong>
                <small>{device.sku}</small>
              </span>
            </button>
          ))}
        </div>
      </Modal>
      <Modal
        open={remarkItemId !== null}
        title="备注"
        okText="保存"
        cancelText="取消"
        onCancel={() => setRemarkItemId(null)}
        onOk={() => {
          if (remarkItemId) updateItem(remarkItemId, { remark: remarkDraft });
          setRemarkItemId(null);
        }}
        destroyOnHidden
      >
        <Input.TextArea
          rows={5}
          value={remarkDraft}
          onChange={(event) => setRemarkDraft(event.target.value)}
          aria-label="设备备注"
        />
      </Modal>
    </SharePageFrame>
  );
}
